import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '../../../components/common/icon/Icon';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { useGetDictionaryCategoriesQuery } from '../../../hooks/api/dictionary/queries/useGetDictionaryCategories.query';
import { IDictionaryCategory } from '../../../interfaces/Dictionary.interfaces';

export const DictionaryChooseTagSection = () => {
  const location = useLocation();
  const [params] = useSearchParams();
  const [isBottomSectionOpen, setIsBottomSectionOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const [headerTitle, setHeaderTitle] = useState<string>('');

  const { data: dictionaryCategories, isLoading: isGetDictionaryCategoriesQueryLoading } =
    useGetDictionaryCategoriesQuery();

  const tagId = useMemo<string | null>(() => {
    if (params.has('tagId')) {
      return params.get('tagId');
    }
    return null;
  }, [params]);

  const searchTerm = useMemo<string | null>(() => {
    if (params.has('searchTerm')) {
      return params.get('searchTerm');
    }
    return null;
  }, [params]);

  useEffect(() => {
    if (tagId) {
      dictionaryCategories?.forEach((category) =>
        category.tags.forEach((tag) => tag.id === tagId && setHeaderTitle(tag.title)),
      );
    } else if (searchTerm) {
      setHeaderTitle(`"${searchTerm}" 에 대한 검색 결과`);
    } else if (location.pathname.includes('brands')) {
      setHeaderTitle('브랜드 목록');
    }
  }, [dictionaryCategories, tagId, searchTerm, location.pathname]);

  const enableScroll = () => {
    document.body.style.cssText = '';
    document.body.style.overflow = 'unset';
  };

  const disableScroll = () => {
    document.body.style.overflow = 'hidden';
  };

  useEffect(() => {
    if (isBottomSectionOpen) {
      disableScroll();
    } else {
      enableScroll();
    }
    return () => {
      enableScroll();
    };
  }, [isBottomSectionOpen]);

  const sortedCategories = useMemo<IDictionaryCategory[]>(() => {
    if (dictionaryCategories) {
      return (
        // category sorted by weight
        dictionaryCategories
          .sort((a, b) => a.weight - b.weight)
          .map((category) => {
            const newCategory = category;
            // tags sorted by order
            newCategory.tags = category.tags.sort((a, b) => a.order - b.order);
            return category;
          })
      );
    }

    return [];
  }, [dictionaryCategories]);

  if (isGetDictionaryCategoriesQueryLoading) return <AnimateLoading />;

  if (sortedCategories) {
    return (
      <Container>
        <ChooseTagTopSection onClick={() => setIsBottomSectionOpen((prev) => !prev)}>
          <CurrentTag>{headerTitle}</CurrentTag>
          <span style={{ cursor: 'pointer' }}>
            {isBottomSectionOpen ? (
              <Icon name={'ArrowUp'} width={16} height={16} />
            ) : (
              <Icon name={'ArrowDown'} width={16} height={16} />
            )}
          </span>
        </ChooseTagTopSection>

        {isBottomSectionOpen && (
          <ChooseTagBottomSectionBackground>
            <ChooseTagBottomSection>
              {sortedCategories &&
                sortedCategories.map((category) => (
                  <CategoryContainer>
                    <CategoryTitle>{category.title}</CategoryTitle>
                    {category.title === '브랜드 분류' && (
                      <TagTitle
                        isSelectedTag={location.pathname.includes('brands')}
                        onClick={() => {
                          navigate('/dictionary/brands');
                          setIsBottomSectionOpen(false);
                        }}
                      >
                        {'브랜드 목록'}
                      </TagTitle>
                    )}
                    {category.title !== '브랜드 분류' &&
                      category.tags.map((tag) => (
                        <TagTitle
                          isSelectedTag={tag.id === tagId}
                          onClick={() => {
                            navigate(`/dictionary/machines?tagId=${tag.id}`);
                            setIsBottomSectionOpen(false);
                          }}
                        >
                          {tag.title}
                        </TagTitle>
                      ))}
                  </CategoryContainer>
                ))}
            </ChooseTagBottomSection>
          </ChooseTagBottomSectionBackground>
        )}
      </Container>
    );
  }

  return <></>;
};

const Container = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const ChooseTagTopSection = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;

  position: relative;

  padding: 16px;

  cursor: pointer;
`;

const CurrentTag = styled.span`
  color: #232429;
  font-size: 18px;
  font-weight: 500;
  line-height: 100%; /* 18px */

  padding: 2px 0px;
`;

const ChooseTagBottomSectionBackground = styled.div`
  width: 100%;
  height: 100vh;

  background: rgba(35, 36, 41, 0.2);
  backdrop-filter: blur(4px);

  z-index: 10;
`;

const ChooseTagBottomSection = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  gap: 12px;

  z-index: 15;

  background-color: white;

  padding: 0px 16px 16px 16px;

  transform: translateY(-8px);
`;

const CategoryContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  gap: 8px;

  padding: 4px 16px;
`;

const CategoryTitle = styled.h2`
  color: #445eff;
  font-size: 10px;
  font-weight: 500;
  line-height: 100%; /* 10px */
`;

const TagTitle = styled.h3<{ isSelectedTag: boolean }>`
  color: ${(props) => (props.isSelectedTag ? '#232429' : '#C4C5CA')};
  font-weight: 500;
  line-height: 100%; /* 16px */

  padding: 4px 0px;

  cursor: pointer;
`;
